import { Box, Stack, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import type { AiCapability } from "../../types";
import { AiDetailCard } from "./AiDetailCard";
import { PRICING_DESIGN_VALUES } from "./pricingDesignValues";

interface AiCapabilityCardProps {
  capability: AiCapability;
  icon: ReactNode;
}

export const AiCapabilityCard = ({
  capability,
  icon,
}: AiCapabilityCardProps) => {
  const theme = useTheme();
  const border = `1px solid ${theme.palette.neutral["010"]}`;
  const mutedText = theme.palette.lightBg.low;

  return (
    <Stack
      sx={{
        gap: "12px",
        minWidth: 0,
        border,
        borderRadius: "16px",
        backgroundColor: theme.palette.neutral.main,
        overflow: "hidden",
      }}
    >
      <Stack
        sx={{
          gap: "16px",
          padding: "24px",
          borderBottom: border,
          backgroundColor: theme.palette.primitive.system.violet[25],
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            display: "grid",
            placeItems: "center",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: theme.palette.primitive.system.violet[100],
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          component="h3"
          sx={PRICING_DESIGN_VALUES.titleText}
        >
          {capability.title}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1.5fr",
            gap: "24px",
          }}
        >
          {capability.metrics.map((metric, index) => (
            <Stack
              key={metric.value}
              // PH-8 · divider on the first column's right edge; the grid gap
              // separates it from the second column's text (design screenshots).
              sx={index === 0 ? { borderRight: border } : undefined}
            >
              <Typography
                variant="h4"
                sx={PRICING_DESIGN_VALUES.metricValueText}
              >
                {metric.value}
              </Typography>
              <Typography variant="body-small" sx={{ color: mutedText }}>
                {metric.label}
              </Typography>
            </Stack>
          ))}
        </Box>
        {capability.notes && (
          <Stack>
            {capability.notes.map((line) => (
              <Typography
                key={line}
                variant="body-small"
                sx={{ color: mutedText }}
              >
                {line}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>

      <Stack sx={{ gap: "12px", padding: "0 12px 12px" }}>
        {capability.details.map((detail) => (
          <AiDetailCard key={detail.key} detail={detail} />
        ))}
      </Stack>
    </Stack>
  );
};
